import { useParams } from "react-router-dom";
import { ArrowLeft, Eye, ThumbsUp, Calendar, Tag, BookOpen, MapPin, Play, MessageSquare, Star, Share2 } from "lucide-react";
import Navigation from "@/components/Navigation";

const defaultListingImg = "/default-listing-fallback.png";
import SEO from "@/components/SEO";
import { VideoPlayer } from "@/components/VideoPlayer";
import { useVideo, useVideos } from "@/hooks/useVideos";
import { generateVideoMetaDescription } from "@/utils/metaDescriptions";
import { generateVideoTitle } from "@/utils/seoTitles";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SITE_URL } from "@/constants/seo";
import Footer from "@/components/Footer";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Home } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate, Link } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useEffect, useState } from "react";
import { SpiderwebLink } from "@/components/interlinking/SpiderwebLink";
import { RelatedInterlinks } from "@/components/interlinking/RelatedInterlinks";
import { supabase } from "@/integrations/supabase/client";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { SocialShare } from "@/components/listing/SocialShare";
import { ProgramCTA } from "@/components/content/ProgramCTA";

const VideoDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: video, isLoading } = useVideo(slug!);
  const { data: relatedVideos } = useVideos({ categoria: video?.categoria, limit: 4 });
  const [articles, setArticles] = useState<any[]>([]);
  const [listings, setListings] = useState<any[]>([]);
  const [comments, setComments] = useState<any[]>([]);
  const [commentName, setCommentName] = useState("");
  const [commentEmail, setCommentEmail] = useState("");
  const [commentText, setCommentText] = useState("");
  const [userRating, setUserRating] = useState<number>(0);
  const [hoveredStar, setHoveredStar] = useState<number>(0);


  useEffect(() => {
    loadRelatedContent();
    if (video?.id) {
      loadComments();
    }
  }, [video?.id]);

  const loadComments = async () => {
    if (!video?.id) return;
    
    const { data } = await supabase
      .from('video_comments_public')
      .select('*')
      .eq('video_id', video.id)
      .order('created_at', { ascending: false });

    setComments(data || []);
  };

  const loadRelatedContent = async () => {
    if (!video) return;

    // Load articles - prioritize by category match
    const { data: articlesData } = await supabase
      .from('articles')
      .select('*')
      .eq('status', 'published')
      .limit(20);

    // Sort articles by relevance to video category
    const sortedArticles = (articlesData || [])
      .sort((a, b) => {
        const categoryMatch = (article: any) => {
          const videoCat = video.categoria?.toLowerCase() || '';
          const articleCat = article.category?.name?.toLowerCase() || '';
          return articleCat.includes(videoCat) || videoCat.includes(articleCat) ? 1 : 0;
        };
        return categoryMatch(b) - categoryMatch(a);
      })
      .slice(0, 2);

    setArticles(sortedArticles);

    // Load listings - prioritize by city first, then activity
    const { data: listingsData } = await supabase
      .from('listings')
      .select('*')
      .eq('status', 'active')
      .limit(50);

    // Sort listings by relevance: city > category match > premium > rating
    const sortedListings = (listingsData || [])
      .sort((a, b) => {
        // City match is highest priority (3 points)
        // Note: video.ciudad may not exist, use safe access
        const videoCity = (video as any).ciudad?.toLowerCase() || '';
        const cityMatchA = videoCity && a.city?.toLowerCase() === videoCity ? 3 : 0;
        const cityMatchB = videoCity && b.city?.toLowerCase() === videoCity ? 3 : 0;
        
        // Activity/category match is second priority (2 points)
        const videoCat = video.categoria?.toLowerCase() || '';
        const categoryMatchA = a.type?.toLowerCase().includes(videoCat) || videoCat.includes(a.type?.toLowerCase() || '') ? 2 : 0;
        const categoryMatchB = b.type?.toLowerCase().includes(videoCat) || videoCat.includes(b.type?.toLowerCase() || '') ? 2 : 0;
        
        // Premium and rating are tiebreakers (1 point)
        const premiumA = a.is_premium ? 1 : 0;
        const premiumB = b.is_premium ? 1 : 0;
        const ratingA = (a.rating || 0) / 10; // Normalize to 0-1
        const ratingB = (b.rating || 0) / 10;
        
        const scoreA = cityMatchA + categoryMatchA + premiumA + ratingA;
        const scoreB = cityMatchB + categoryMatchB + premiumB + ratingB;
        
        return scoreB - scoreA;
      })
      .slice(0, 2);

    setListings(sortedListings);
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!commentName || !commentText) {
      toast.error('Nombre y comentario son requeridos');
      return;
    }

    try {
      await supabase.from('video_comments').insert({
        video_id: video.id,
        user_name: commentName,
        user_email: commentEmail,
        comment: commentText
      });

      toast.success('Comentario enviado. Será visible una vez aprobado.');
      setCommentName("");
      setCommentEmail("");
      setCommentText("");
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast.error('Error al enviar comentario');
    }
  };

  const handleRating = async (rating: number) => {
    setUserRating(rating);
    toast.success(`Has calificado este video con ${rating} estrellas`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 pt-32 lg:pt-40 text-center">
          <h1 className="text-2xl font-bold mb-4">Video no encontrado</h1>
          <Button onClick={() => navigate("/videos")}>Volver a Videos</Button>
        </div>
      </div>
    );
  }

  // Use YouTube thumbnail as it's always reliable
  const videoImage = video.thumbnail_url?.startsWith('http') && !video.thumbnail_url.includes('blob.core.windows.net')
    ? video.thumbnail_url 
    : `https://img.youtube.com/vi/${video.youtube_id}/maxresdefault.jpg`;
  
  const absoluteImageUrl = videoImage;
  const videoUrl = `https://ekselencia.com/video/${video.slug}`;

  // Generate unique meta description
  const metaDescription = generateVideoMetaDescription({
    titulo: video.titulo,
    descripcion: video.descripcion,
    categoria: video.categoria,
    vistas: video.vistas,
    votos_positivos: video.votos_positivos,
    creado_en: video.creado_en,
    ciudad: (video as any).ciudad
  });

  // Breadcrumb items
  const breadcrumbItems = [
    { name: "Inicio", path: "/" },
    { name: "Videos", path: "/videos" },
    { name: video.categoria, path: `/videos?categoria=${encodeURIComponent(video.categoria)}` },
    { name: video.titulo }
  ];

  // Structured data for breadcrumbs
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbItems.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      ...(item.path ? { "item": `${SITE_URL}${item.path}` } : {})
    }))
  };

  // Structured data for video
  const videoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: video.titulo,
    description: video.descripcion || video.titulo,
    thumbnailUrl: absoluteImageUrl,
    uploadDate: video.publicado_en || video.creado_en,
    embedUrl: `https://www.youtube.com/embed/${video.youtube_id}`,
    contentUrl: video.video_url || `https://www.youtube.com/watch?v=${video.youtube_id}`,
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={generateVideoTitle({
          title: video.titulo,
          category: video.categoria,
          tags: video.etiquetas
        })}
        description={metaDescription}
        ogImage={absoluteImageUrl}
        structuredData={[breadcrumbSchema, videoSchema]}
      />
      <Navigation />

      <main className="container mx-auto px-4 pt-32 lg:pt-40 pb-12">
        {/* Breadcrumbs */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/" className="flex items-center gap-1">
                  <Home className="w-4 h-4" />
                  Inicio
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/videos">Videos</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={`/videos?categoria=${encodeURIComponent(video.categoria)}`}>
                  {video.categoria}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="max-w-[150px] truncate md:max-w-none">
                {video.titulo}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <Button
          variant="ghost"
          onClick={() => navigate("/videos")}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a Videos
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <VideoPlayer youtubeId={video.youtube_id} title={video.titulo} />

            <div className="mt-6">
              <h1 className="text-3xl font-bold mb-4">{video.titulo}</h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  <span>{video.vistas} visualizaciones</span>
                </div>
                <div className="flex items-center gap-2">
                  <ThumbsUp className="w-4 h-4" />
                  <span>{video.votos_positivos} me gusta</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {format(new Date(video.creado_en), "d 'de' MMMM, yyyy", { locale: es })}
                  </span>
                </div>
              </div>

              {video.descripcion && (
                <Card className="mb-6">
                  <CardContent className="pt-6">
                    <h2 className="text-xl font-semibold mb-3">Descripción</h2>
                    <p className="text-muted-foreground whitespace-pre-wrap">
                      <SpiderwebLink text={video.descripcion || ''} />
                    </p>
                    {video.info_url && (
                      <div className="mt-4">
                        <Button asChild variant="default">
                          <a href={video.info_url} target="_blank" rel="noopener noreferrer">
                            Más Información
                          </a>
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Rating Section */}
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold mb-4">Califica este video</h3>
                  <div className="flex items-center gap-2 mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => handleRating(star)}
                        onMouseEnter={() => setHoveredStar(star)}
                        onMouseLeave={() => setHoveredStar(0)}
                        className="transition-transform hover:scale-110"
                      >
                        <Star
                          className={`w-8 h-8 ${
                            star <= (hoveredStar || userRating)
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                    {userRating > 0 && (
                      <span className="ml-2 text-sm text-muted-foreground">
                        {userRating} de 5 estrellas
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Social Share Section */}
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Share2 className="w-5 h-5" />
                    Compartir este video
                  </h2>
                  <SocialShare 
                    url={videoUrl}
                    title={video.titulo}
                    description={video.descripcion || "Mira este video de entrenamiento y fitness"}
                  />
                </CardContent>
              </Card>

              <Separator className="my-8" />

              {/* Comments Section */}
              <div className="space-y-6">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <MessageSquare className="w-6 h-6" />
                  Comentarios ({comments.length})
                </h2>

                <Card>
                  <CardContent className="pt-6">
                    <form onSubmit={handleSubmitComment} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          placeholder="Tu nombre *"
                          value={commentName}
                          onChange={(e) => setCommentName(e.target.value)}
                          required
                        />
                        <Input
                          type="email"
                          placeholder="Tu email (opcional)"
                          value={commentEmail}
                          onChange={(e) => setCommentEmail(e.target.value)}
                        />
                      </div>
                      <Textarea
                        placeholder="Escribe tu comentario... *"
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        rows={4}
                        required
                      />
                      <Button type="submit">Enviar Comentario</Button>
                    </form>
                  </CardContent>
                </Card>

                <div className="space-y-4">
                  {comments.map((comment) => (
                    <Card key={comment.id}>
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold">{comment.user_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(comment.created_at).toLocaleDateString('es-ES')}
                            </p>
                          </div>
                        </div>
                        <p className="text-foreground">{comment.comment}</p>
                      </CardContent>
                    </Card>
                  ))}
                  {comments.length === 0 && (
                    <Card>
                      <CardContent className="pt-6 text-center text-muted-foreground">
                        No hay comentarios todavía. ¡Sé el primero en comentar!
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1 space-y-6">
            {/* Add Video Button for Registered Users */}
            <Card className="bg-gradient-primary text-white">
              <CardContent className="pt-6">
                <h3 className="font-bold mb-3 flex items-center gap-2">
                  <Play className="w-5 h-5" />
                  ¿Tienes un video de tu deporte favorito?
                </h3>
                <p className="text-sm mb-4 opacity-90">
                  Comparte tus mejores videos con nuestra comunidad
                </p>
                <Link to="/submit-video">
                  <Button variant="secondary" className="w-full">
                    Enviar Video
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Program CTA */}
            <ProgramCTA 
              categoryName={video.categoria || ""} 
              variant="sidebar"
            />

            {/* Video Details Card */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Detalles del Video
                </h3>

                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Categoría</p>
                    <Badge 
                      variant="secondary"
                      className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                      onClick={() => navigate(`/videos?categoria=${encodeURIComponent(video.categoria)}`)}
                    >
                      {video.categoria}
                    </Badge>
                  </div>

                  {video.etiquetas && video.etiquetas.length > 0 && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Etiquetas</p>
                      <div className="flex flex-wrap gap-2">
                        {video.etiquetas.map((tag, index) => (
                          <Badge 
                            key={index} 
                            variant="outline"
                            className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                            onClick={() => navigate(`/videos?search=${encodeURIComponent(tag)}`)}
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Related Articles */}
            {articles.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <BookOpen className="w-5 h-5" />
                    Artículos Relacionados
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {articles.map((article) => (
                    <Link key={article.id} to={`/articulo/${article.slug}?ref=GEN-9PP3AZ`}>
                      <div className="border rounded-lg p-3 hover:bg-accent transition-colors">
                        {(article.thumbnail_url ?? article.image_url) && (
                          <img
                            src={article.thumbnail_url ?? article.image_url}
                            alt={article.title}
                            className="w-full h-32 object-cover rounded-md mb-2"
                          />
                        )}
                        <h4 className="font-semibold text-sm mb-1 line-clamp-2">{article.title}</h4>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {article.summary}
                        </p>
                      </div>
                    </Link>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Related Gyms */}
            {listings.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MapPin className="w-5 h-5" />
                    Gimnasios Destacados
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {listings.map((listing) => (
                    <Link key={listing.id} to={`/listing/${listing.slug || listing.id}`}>
                      <div className="border rounded-lg p-3 hover:bg-accent transition-colors">
                        <img
                          src={
                            listing.images && listing.images.length > 0
                              ? listing.images[0]
                              : listing.thumbnail_url
                              ? listing.thumbnail_url
                              : listing.is_premium
                              ? listing.images?.[0] || defaultListingImg
                              : defaultListingImg
                          }
                          alt={listing.name}
                          className="w-full h-32 object-cover rounded-md mb-2"
                          onError={(e) => {
                            e.currentTarget.src = defaultListingImg;
                          }}
                        />
                        <h4 className="font-semibold text-sm mb-1 line-clamp-1">{listing.name}</h4>
                        <p className="text-xs text-muted-foreground line-clamp-1 mb-2">
                          {listing.city}
                        </p>
                        <Badge variant="secondary" className="text-xs">
                          ⭐ {listing.rating || 5.0}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Related Videos Section */}
        {relatedVideos && relatedVideos.length > 1 && (
          <>
            <Separator className="my-12" />
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Play className="w-6 h-6" />
                Videos Relacionados
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedVideos
                  .filter(v => v.id !== video.id)
                  .slice(0, 3)
                  .map((relatedVideo) => (
                    <Link key={relatedVideo.id} to={`/video/${relatedVideo.slug}?ref=GEN-9PP3AZ`}>
                      <Card className="h-full hover:shadow-lg transition-shadow">
                        <img
                          src={relatedVideo.thumbnail_url || `https://img.youtube.com/vi/${relatedVideo.youtube_id}/hqdefault.jpg`}
                          alt={relatedVideo.titulo}
                          className="w-full aspect-video object-cover rounded-t-lg"
                          width="400"
                          height="225"
                          loading="lazy"
                        />
                        <CardContent className="pt-4">
                          <Badge variant="secondary" className="mb-2">
                            {relatedVideo.categoria}
                          </Badge>
                          <h3 className="font-semibold mb-2 line-clamp-2">{relatedVideo.titulo}</h3>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {relatedVideo.vistas}
                            </div>
                            <div className="flex items-center gap-1">
                              <ThumbsUp className="w-3 h-3" />
                              {relatedVideo.votos_positivos}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
              </div>
            </div>
          </>
        )}

        {/* Related Interlinks */}
        <div className="container mx-auto px-4 pb-8">
          <RelatedInterlinks text={[video.titulo, video.descripcion, video.categoria].filter(Boolean).join(' ')} maxLinks={4} />
        </div>
      </main>
    </div>
  );
};

export default VideoDetail;
